import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import BoltIcon from '@mui/icons-material/Bolt'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import Link from 'next/link'
import Router from 'next/router'
import React, { useCallback, useEffect, useMemo } from 'react'
import LandingSearch from 'src/components/AutoComplete/LandingSearch'
import { PillarPage } from 'src/constants/searchPages'
import useEffectiveCity from 'src/hooks/useEffectiveCity'
import useLastSearch from 'src/hooks/useLastSearch'
import useSupplyLead from 'src/hooks/useSupplyLead'
import { resolveOnDemandCity } from 'src/utils/onDemand'
import ga from 'src/utils/pixels/gtag'
import heroMobile from '../../../public/images/stock/home-hero-storage-mobile.webp'
import heroDesktop from '../../../public/images/stock/home-hero-storage.webp'
import {
  IGoogleAutocompleteSuggestion,
  IGooglePlaceDetails
} from '../../types/graphql'
import { RedirectUrls } from '../../types/types'
import LastSearchCard from './LastSearchCard'

// Escape `< > & "` so an i18n value containing an angle bracket can never
// break out of the dangerouslySetInnerHTML payload below. Defensive — locale
// JSON in this repo is author-controlled, but cheap insurance. See CLAUDE.md
// "Trigger H — FB IAB strips React `<!-- -->` boundary markers".
const escapeForHtml = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

// Direct import (not dynamic) — Next 16 + React 19 streaming SSR was
// serializing a dynamic-import Suspense fallback into the SSR HTML on `/`,
// then hydrating with the resolved MUI Autocomplete → classic #418 (the
// errorStack frames in Datadog were all React-DOM internals because React
// throws inside its hydration commit before any application frames). Direct
// importing removes the Suspense boundary entirely so SSR and CSR render the
// same Autocomplete from the same module. Bundle cost (~50KB MUI Autocomplete
// tree) is acceptable for the home page where this is the only secondary
// search affordance.

const VALUE_PROP_ICONS: Record<string, React.ReactNode> = {
  fast: <BoltIcon className="text-base text-blue-300" />,
  cheap: <EventAvailableIcon className="text-base text-blue-300" />
}

type Chip = {
  key: string
  label: string
  href: string
  intent: 'demand' | 'b2b' | 'on-demand'
  pillar?: PillarPage | 'flotillas' | 'on-demand'
}

/** Threshold at which the city-inlined CTA label blows the pill budget and
 *  forces the SHORT fallback. NOTE: recalibrated 2026-06-06 from 12 → 8.
 *  The labels grew ("Ver estacionamientos en {{city}}" — a 24-char prefix
 *  BEFORE the city) without the threshold being updated from its original
 *  calibration for the old shorter "Ver inventario en {{city}}" label.
 *  Result: medium cities like "Tapachula" (9) kept the full label, which —
 *  with two CTAs constrained side-by-side in a max-w-2xl row and no
 *  `whitespace-nowrap` — wrapped to two lines and doubled the button height
 *  (reported 2026-06-06). 8 keeps the personalized full label only for the
 *  shortest cities (León, Puebla, Cancún, Mérida, Toluca); everything else
 *  uses the compact SHORT label. The `whitespace-nowrap` + `md:flex-wrap`
 *  on the CTA row below are the belt-and-suspenders so no label can ever
 *  wrap inside a button regardless of city length. */
const LONG_CITY_THRESHOLD = 8

/**
 * Pre-paint layout reservation for the resume card slot below.
 *
 * `LastSearchCard` reads localStorage, so React cannot know whether it
 * renders until hydration — ~1.3s after paint on prod. Mounting it then
 * inserted 82px into this hero's flow, pushing the CTA row, chips, search
 * fallback and stat card down AND re-cropping the `bg-cover bg-center`
 * background photo, which reads as the hero image jumping down (measured
 * 2026-08-11: CLS 0.019 desktop for every returning visitor).
 *
 * This runs synchronously while the parser is still above the hero, so the
 * flag is set before the slot is laid out and the card lands in space that
 * was always reserved. It deliberately targets <html> — the only element
 * here that React does not own. Stamping the slot itself would mutate
 * React-owned DOM before hydration, which is the exact class of bug the
 * anti-#418 script in `pages/_document.tsx` exists to clean up.
 *
 * It is an approximation of the real gate (no enum validation), and
 * `useLastSearch` re-syncs the attribute once the true payload resolves, so
 * a stale entry collapses the slot instead of leaving an empty gap.
 *
 * Storage access sits inside the try/catch because iOS Safari with storage
 * disabled throws on the `sessionStorage`/`localStorage` getter itself, not
 * just on `getItem` — see the project's safeStorage rule. The keys and the
 * 30-day TTL mirror `src/hooks/useLastSearch.ts`; if they change there,
 * change them here too. Drift degrades to today's behavior — no reservation
 * — never to a wrong one.
 */
const RESUME_RESERVATION_SCRIPT =
  "(function(){try{if(window.sessionStorage.getItem('spotme:last_search_dismissed')==='1')return;var r=window.localStorage.getItem('spotme:last_search');if(!r)return;var p=JSON.parse(r);if(!p||typeof p.pillar!=='string'||typeof p.city!=='string'||typeof p.timestamp!=='number')return;if(Date.now()-p.timestamp>2592000000)return;document.documentElement.setAttribute('data-resume-search','1')}catch(e){}})();"

const Hero = () => {
  const { t } = useTranslation('landing')
  const { t: tStats } = useTranslation('platform_stats')
  const statsInterp = useMemo(
    () => ({
      cities: tStats('CITIES'),
      spaces: tStats('SPACES')
    }),
    [tStats]
  )

  const { city, cityName, source, isReady } = useEffectiveCity()
  const cityKnown = isReady && source !== 'fallback' && cityName.length > 0
  const longCity = cityName.length >= LONG_CITY_THRESHOLD

  const { saveLastSearch } = useLastSearch()
  const { supplyLead } = useSupplyLead()

  const VALUE_PROPS: { key: string; text: string }[] = t(
    'INDEX_HERO.VALUE_PROPS',
    { returnObjects: true, ...statsInterp }
  )

  // Primary CTA → highest-converting destination (storage × user-city).
  // Falls back to the pillar landing (no city) when geo hasn't resolved.
  const primaryHref = cityKnown
    ? `/${PillarPage.SelfStorage}/${city}`
    : `/${PillarPage.SelfStorage}`

  const primaryLabel = cityKnown
    ? longCity
      ? t('INDEX_HERO.PRIMARY_CTA_CITY_SHORT')
      : t('INDEX_HERO.PRIMARY_CTA_CITY', { city: cityName })
    : t('INDEX_HERO.PRIMARY_CTA_FALLBACK')

  // Secondary CTA → parking, B2C peer of self-storage. The H1 promises
  // "bodegas y estacionamientos"; this fulfills the second half. Quieter
  // chrome (outlined, no glow) preserves storage as the loudest single
  // CTA while giving parking-intent users a clear path that doesn't
  // require a chip-row scan.
  const parkingHref = cityKnown
    ? `/${PillarPage.Parking}/${city}`
    : `/${PillarPage.Parking}`

  const parkingLabel = cityKnown
    ? longCity
      ? t('INDEX_HERO.PARKING_CTA_CITY_SHORT')
      : t('INDEX_HERO.PARKING_CTA_CITY', { city: cityName })
    : t('INDEX_HERO.PARKING_CTA_FALLBACK')

  // On-demand chip — destination + price suffix vary by resolved city.
  // Covered metros route to the city's almacenamiento-a-domicilio page;
  // everywhere else falls back to the generic landing.
  const onDemandHref = useMemo(() => {
    const resolved = cityKnown ? resolveOnDemandCity(city) : null
    return resolved?.partnerActive
      ? `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio/${resolved.config.canonicalSlug}`
      : `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio`
  }, [cityKnown, city])

  // Chip rail = specialty pillars only (B2B warehouse / B2B industrial /
  // B2B fleet parking / on-demand). Retail parking lives in the secondary
  // CTA above — it's a B2C peer of self-storage, NOT a B2B specialty.
  // Mixing it with warehouse/industrial here would flatten the
  // demand/supply hierarchy and underweight a core pillar.
  const chips: Chip[] = useMemo(() => {
    const warehouse = cityKnown
      ? `/${PillarPage.Warehouse}/${city}`
      : `/${PillarPage.Warehouse}`
    const industrial = cityKnown
      ? `/${PillarPage.Industrial}/${city}`
      : `/${PillarPage.Industrial}`

    return [
      {
        key: 'warehouse',
        label: t('INDEX_HERO.CHIPS.WAREHOUSE'),
        href: warehouse,
        intent: 'b2b',
        pillar: PillarPage.Warehouse
      },
      {
        key: 'industrial',
        label: t('INDEX_HERO.CHIPS.INDUSTRIAL'),
        href: industrial,
        intent: 'b2b',
        pillar: PillarPage.Industrial
      },
      {
        key: 'flotillas',
        label: t('INDEX_HERO.CHIPS.FLOTILLAS'),
        href: '/pension-estacionamiento/flotillas',
        intent: 'b2b',
        pillar: 'flotillas'
      },
      {
        key: 'on-demand',
        label: t('INDEX_HERO.CHIPS.ON_DEMAND_SHORT'),
        href: onDemandHref,
        intent: 'on-demand',
        pillar: 'on-demand'
      }
    ]
  }, [t, cityKnown, city, onDemandHref])

  useEffect(() => {
    Router.prefetch(RedirectUrls.BeginSearch)
    Router.prefetch(primaryHref)
    Router.prefetch(parkingHref)
  }, [primaryHref, parkingHref])

  const handlePrimaryClick = useCallback(() => {
    saveLastSearch({ pillar: PillarPage.SelfStorage, city })
    ga.event('hero_cta_click', {
      pillar: PillarPage.SelfStorage,
      city,
      source: 'primary'
    })
  }, [saveLastSearch, city])

  const handleParkingClick = useCallback(() => {
    saveLastSearch({ pillar: PillarPage.Parking, city })
    ga.event('hero_cta_click', {
      pillar: PillarPage.Parking,
      city,
      source: 'primary_parking'
    })
  }, [saveLastSearch, city])

  const handleChipClick = useCallback(
    (chip: Chip) => {
      if (chip.pillar && chip.pillar in PillarPage) {
        // Persist for the LastSearchCard the next time around. Skip for
        // flotillas / on-demand — they're not pillar-shaped destinations
        // and don't map onto the LastSearchPayload schema.
        saveLastSearch({ pillar: chip.pillar as PillarPage, city })
      }
      ga.event('hero_cta_click', {
        pillar: chip.pillar ?? null,
        city,
        source: 'chip',
        intent: chip.intent
      })
    },
    [saveLastSearch, city]
  )

  const handleSupplyClick = useCallback(() => {
    supplyLead({ funnel: 'individual', source: 'hero' })
    ga.event('hero_cta_click', {
      source: 'supply',
      city
    })
  }, [supplyLead, city])

  const handleSearchSubmit = useCallback(
    (
      place: IGoogleAutocompleteSuggestion | undefined,
      search: string,
      coords?: IGooglePlaceDetails | undefined
    ) => {
      const { description, placeId } = place ?? {}
      ga.event('search', { search, description })
      ga.event('hero_cta_click', {
        source: 'search_fallback',
        city
      })
      Router.push({
        pathname: RedirectUrls.BeginSearch,
        query: {
          description,
          placeId,
          ...(coords?.lat && coords?.lng
            ? { lat: coords.lat, lng: coords.lng }
            : {})
        }
      })
    },
    [city]
  )

  return (
    <section className="relative w-full overflow-hidden bg-ink-800 pt-[calc(var(--nav-h)+24px)] pb-12 md:flex md:min-h-svh md:flex-col md:justify-center md:pt-[calc(var(--nav-h)+40px)] md:pb-12 short-desk:pt-[calc(var(--nav-h)+24px)] short-desk:pb-7">
      {/* Desktop height = the window, not the content stack (2026-09-27).
          The stack alone measured 938px at every width while 70% of desktop
          home visitors have a window under 860px tall (PostHog, 60d): the
          fold cut the chips, search and stat card on laptops, and a 1080px
          screen got a 142px white strip. The section now fills 100svh with
          the content centred; `short-desk` (global.css) tightens the rhythm
          on short windows. Same pattern as the /anfitrion hero. */}
      {/* The hero photo is the LCP candidate on both breakpoints and it is
          a CSS background (see below), which the browser only discovers
          when it lays out the element. Preload it, one file per
          breakpoint, so the fetch starts with the HTML. The media queries
          mirror the `md` breakpoint (900px). */}
      <Head>
        <link
          rel="preload"
          as="image"
          href={heroMobile.src}
          media="(max-width: 899px)"
          fetchPriority="high"
        />
        <link
          rel="preload"
          as="image"
          href={heroDesktop.src}
          media="(min-width: 900px)"
          fetchPriority="high"
        />
      </Head>

      {/* Photo = the product. A corridor of mini-bodega units, storage-
          first because storage is the primary CTA and where the paid
          spend goes (the 2026-02 photo was a European EV parking lot with
          a composited phone — off-message, and invisible on mobile under
          a 90% scrim). Parking keeps its place in the H1 + secondary CTA.

          Decorative atmosphere as CSS background-image, NOT <Image fill>.
          See CLAUDE.md "Trigger J — bare <Image fill> still fires #418 even
          with placeholder=empty". The `fill` prop alone injects a 7-property
          inline style block at SSR; iOS Safari cached-image fast paths mutate
          the live DOM style during hydration → React 19 strict attribute
          comparison fires #418. CSS background-image keeps the <img> out of
          the React tree entirely. Decorative, so aria-hidden replaces alt.

          suppressHydrationWarning on the image divs: inline background-image
          url() drifts (`url(x)` SSR vs browser-normalized `url("x")` CSR).
          Genuine React-tree style mismatch → suppress keeps SSR DOM, no
          fallback. See CLAUDE.md "Trigger D Phase 5". */}

      {/* DESKTOP backdrop — full-bleed behind the whole section. The scrim
          is DIRECTIONAL, not a blanket: copy lives in the LEFT column, so it
          runs left→right and lets the photo through on the right where the
          unit doors are. A second, bottom-up scrim grounds the rating card
          (right rail, bottom) and eases the section into the next one. Any
          change here must be re-checked for headline contrast at 1440. */}
      <div className="absolute inset-0 z-0 hidden md:block">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url("${heroDesktop.src}")` }}
          suppressHydrationWarning
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-800/95 from-20% via-ink-800/70 via-50% to-ink-800/15" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink-800/85 to-transparent" />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-20 right-[-10%] size-[40rem] rounded-full bg-blue-500/15 blur-3xl"
        />
      </div>

      {/* MOBILE backdrop — full-bleed BEHIND the copy, same as desktop and
          the pre-2026-09-06 hero. The 09-06 deploy put the photo in a
          300px band in normal flow ABOVE the copy; that pushed every CTA
          ~100-200px down the phone and mobile hero CTR fell 39.6% → 24.8%
          over two pooled weeks (insight yUAVe9ad, 2026-09-21 read) while
          desktop — same photo, no layout change — stayed flat. The scrim
          is lighter than the old 90% blanket so the units still read, but
          it is a legibility device first: re-check H1 contrast at 320
          before touching it. */}
      <div className="absolute inset-0 z-0 md:hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-position-[50%_40%] bg-no-repeat"
          style={{ backgroundImage: `url("${heroMobile.src}")` }}
          suppressHydrationWarning
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-800/70 via-ink-800/75 via-40% to-ink-800/90" />
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 gap-10 px-5 md:grid-cols-12 md:gap-12 md:px-10">
        {/* Left rail — primary content */}
        <div className="md:col-span-8 md:col-start-1">
          {/* Eyebrow + live indicator */}
          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="relative inline-flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-blue-300 opacity-70" />
              <span className="relative inline-flex size-2 rounded-full bg-blue-400" />
            </span>
            <span className="font-display text-[10px] tracking-[0.14em] text-blue-200 uppercase md:text-xs md:tracking-[0.22em]">
              {t('INDEX_HERO.BADGE')}
            </span>
          </div>

          {/* Headline — "Renta bodegas y estacionamientos *en {{city}}.*"
              Italic Fraunces on the city as the editorial signature. The
              SEO-rich phrase ("bodegas y estacionamientos") lives in the
              H1 rather than a soft "espacios" because the H1 carries
              keyword weight for branded + direct queries that land here.

              dangerouslySetInnerHTML is deliberate — see CLAUDE.md
              "Trigger H — FB IAB strips React `<!-- -->` boundary markers".
              JSX with text→span→text siblings serializes at SSR with React
              boundary comments between each child. Facebook's in-app browser
              (FB_IAB) strips HTML comments aggressively, leaving the CSR DOM
              without them — React 19 strict-compares the byte stream and
              fires #418. Inlining via innerHTML emits a single continuous
              text/element stream with no boundaries for FB IAB to strip. */}
          <h1
            className="mt-5 max-w-3xl text-[clamp(32px,9vw,42px)] leading-[1.05] font-semibold tracking-[-0.02em] text-white-500 md:mt-6 md:text-[56px] md:leading-[1.02] lg:text-[64px] short-desk:mt-3.5 short-desk:text-[50px]"
            dangerouslySetInnerHTML={{
              __html: `${escapeForHtml(t('INDEX_HERO.HEADING_PREFIX'))} <span class="font-semibold">${escapeForHtml(t('INDEX_HERO.HEADING_MAIN'))}</span> ${escapeForHtml(t('INDEX_HERO.HEADING_IN'))} <span class="font-display text-[0.95em] font-normal text-blue-500 italic">${escapeForHtml(
                cityKnown
                  ? `${cityName}.`
                  : t('INDEX_HERO.HEADING_CITY_FALLBACK')
              )}</span>`
            }}
          />

          <p className="mt-4 max-w-xl text-base leading-relaxed text-white-500/85 md:mt-5 md:text-lg short-desk:mt-3">
            {t('INDEX_HERO.SUBHEADING', statsInterp)}
          </p>

          {/* Returning-visitor resume card — same component as the control
              hero. Sits above the primary CTA so direct traffic can resume
              in one tap.

              `resume-card-slot` reserves the card's exact height before first
              paint for visitors who have a stored search — see
              RESUME_RESERVATION_SCRIPT above for why, and `--resume-card-h`
              in public/styles/global.css for the height token both the slot
              and the card read. Without it the card mounts ~1.3s in and
              shoves everything below it — plus the background photo's crop —
              down by 82px. */}
          <script
            dangerouslySetInnerHTML={{ __html: RESUME_RESERVATION_SCRIPT }}
          />
          <div className="resume-card-slot mt-5 max-w-2xl md:mt-5 short-desk:mt-3.5">
            <LastSearchCard />
          </div>

          {/* CTA pair — storage primary (loud blue-500 + glow), parking
              secondary (outlined, no glow). The H1 promises bodegas Y
              estacionamientos; this fulfills both. Visual weight carries
              the hierarchy: filled+shadow vs outlined+transparent reads
              clearly as primary vs secondary even when sitting side-by-
              side on desktop. Stacks on mobile per project rule on
              horizontal action groups. */}
          {/* md:flex-wrap so two single-line CTAs gracefully stack instead of
              shrinking + wrapping their text when a long city label is inlined.
              whitespace-nowrap on each button guarantees the label never
              breaks to two lines (the 2026-06-06 "huge buttons" regression).
              See LONG_CITY_THRESHOLD note above. */}
          <div className="mt-5 flex w-full max-w-2xl flex-col gap-3 md:mt-5 md:flex-row md:flex-wrap md:items-center md:gap-3 short-desk:mt-3.5">
            <Link
              href={primaryHref}
              onClick={handlePrimaryClick}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-500 px-7 py-3 text-center text-base font-semibold text-white-500 shadow-[0_20px_40px_-16px_rgba(0,164,255,0.55)] transition-all duration-200 hover:bg-blue-600 active:scale-[0.98] md:w-auto md:px-8 md:text-[17px] md:whitespace-nowrap"
            >
              <span>{primaryLabel}</span>
              <ArrowForwardRoundedIcon className="text-[18px] transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
            <Link
              href={parkingHref}
              onClick={handleParkingClick}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-white-500/25 bg-white-500/[0.04] px-7 py-3 text-center text-base font-semibold text-white-500 backdrop-blur-sm transition-all duration-200 hover:border-white-500/45 hover:bg-white-500/[0.09] active:scale-[0.98] md:w-auto md:px-8 md:text-[17px] md:whitespace-nowrap"
            >
              <span>{parkingLabel}</span>
              <ArrowForwardRoundedIcon className="text-[18px] transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </div>

          {/* Secondary chips — pillar peers, demoted from the primary.
              City-aware hrefs match the existing Hero's chip pattern; same
              chrome too so brand consistency holds inside the variant. */}
          <div className="mt-6 max-w-2xl md:mt-6 short-desk:mt-4.5">
            <p className="mb-2.5 font-display text-[10px] tracking-[0.18em] text-white-500/75 uppercase md:text-[11px] md:tracking-[0.22em]">
              {t('INDEX_HERO.EXPLORE_OTHERS_LABEL')}
            </p>
            <ul className="flex flex-wrap items-center gap-1.5 md:gap-2">
              {chips.map((chip) => (
                <li key={chip.key}>
                  <Link
                    href={chip.href}
                    onClick={() => handleChipClick(chip)}
                    className={`group inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-medium backdrop-blur-sm transition-colors md:px-3.5 md:py-2 md:text-[13px] ${
                      chip.intent === 'on-demand'
                        ? 'border-blue-300/40 bg-blue-500/12 text-white-500 hover:border-blue-300 hover:bg-blue-500/25'
                        : 'border-white-500/15 bg-white-500/[0.06] text-white-500/90 hover:border-blue-300 hover:bg-blue-500/15 hover:text-white-500'
                    }`}
                  >
                    <span>{chip.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Search remains a secondary fallback after the product choices. */}
          <div className="mt-6 max-w-md opacity-60 transition-opacity duration-200 focus-within:opacity-100 hover:opacity-100 md:mt-6 short-desk:mt-4">
            <p className="mb-2 text-[11px] tracking-[0.06em] text-blue-200/60 uppercase md:text-xs">
              {t('INDEX_HERO.SEARCH_FALLBACK_LABEL')}
            </p>
            <LandingSearch
              handleOnSelect={handleSearchSubmit}
              placeholder={t('INDEX_HERO.SEARCH_PHOLDER')}
              buttonLabel={t('INDEX_HERO.SEARCH_BUTTON')}
            />
          </div>

          {/* Value props — same minimal row as control */}
          <ul className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-2 md:mt-7 md:gap-x-6 md:gap-y-3 short-desk:mt-4.5">
            {VALUE_PROPS.map((prop) => (
              <li key={prop.key} className="flex items-center gap-1.5 md:gap-2">
                <span aria-hidden="true">{VALUE_PROP_ICONS[prop.key]}</span>
                <span className="text-[13px] font-medium text-white-500/90 md:text-sm">
                  {prop.text}
                </span>
              </li>
            ))}
          </ul>

          {/* Supply microlink — hairline-divided, low visual weight.
              Fires supply_lead via useSupplyLead, NEVER generateLead
              (demand-side taxonomy is strictly separate). Copy reads as
              "¿Tienes un espacio? Súbelo a SpotMe" — uses the pronoun
              over a noun ("renta tu bodega") so the CTA stays valid for
              parking / warehouse / industrial supply hosts too, not just
              storage. */}
          <div className="mt-7 max-w-2xl border-t border-white-500/10 pt-4 md:mt-8 short-desk:mt-4 short-desk:pt-3">
            <Link
              href={RedirectUrls.Anfitrion}
              onClick={handleSupplyClick}
              className="group inline-flex items-center gap-2 text-sm text-blue-100/70 transition-colors hover:text-white-500"
            >
              <span>{t('INDEX_HERO.SUPPLY_HINT')}</span>
              <span className="font-medium text-blue-300 underline decoration-blue-400/40 underline-offset-[5px] transition-colors group-hover:text-white-500 group-hover:decoration-blue-300">
                {t('INDEX_HERO.SUPPLY_CTA')}
              </span>
              <ArrowForwardRoundedIcon className="text-[14px] transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>

        {/* Right-rail stat card — desktop only. Platform stats the site
            already shows (spaces + cities); no rating — there are no real
            reviews yet. Centred, not bottom-pinned: at the bottom of a 938px
            column it sat under the fold on most laptops. */}
        <aside className="hidden md:col-span-4 md:col-start-9 md:flex md:items-center">
          <div className="w-full border-t border-white-500/15 pt-8">
            <p className="font-display text-6xl leading-none font-medium tracking-[-0.02em] text-white-500 lg:text-7xl short-desk:text-[56px]">
              {tStats('SPACES')}
            </p>
            <p className="mt-3 text-sm text-blue-200/70">
              <span className="text-white-500">{tStats('SPACES_LABEL')}</span>
              {` · ${tStats('CITIES')} ${tStats('CITIES_LABEL')}`}
            </p>
          </div>
        </aside>

        {/* Mobile trust indicator — same stats as the desktop rail */}
        <div className="flex items-center gap-3 md:hidden">
          <span className="text-sm text-blue-200/80">
            <span className="font-semibold text-white-500">
              {tStats('SPACES')}
            </span>
            {` ${tStats('SPACES_LABEL')} · ${tStats('CITIES')} ${tStats('CITIES_LABEL')}`}
          </span>
        </div>
      </div>
    </section>
  )
}

export default React.memo(Hero)
